import { createApp } from '@backstage/app-defaults';
import { RELATION_DEPENDENCY_OF, RELATION_DEPENDS_ON, RELATION_HAS_PART, RELATION_OWNED_BY, RELATION_OWNER_OF, RELATION_PART_OF } from '@backstage/catalog-model';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { AlertDisplay, OAuthRequestDialog, SignInProviderConfig } from '@backstage/core-components';
import { IconComponent, microsoftAuthApiRef } from '@backstage/core-plugin-api';
import CssBaseline from '@mui/material/CssBaseline';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import { CatalogEntityPage, CatalogIndexPage, catalogPlugin } from '@backstage/plugin-catalog';
import { CatalogGraphPage, catalogGraphPlugin } from '@backstage/plugin-catalog-graph';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { orgPlugin } from '@backstage/plugin-org';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { SearchPage } from '@backstage/plugin-search';
import { TechDocsIndexPage, techdocsPlugin, TechDocsReaderPage } from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { VersionSelector } from '@internal/backstage-plugin-techdocs-module-addons';
import { ProviderSettingsItem, UserSettingsPage } from '@backstage/plugin-user-settings';
import { UnifiedThemeProvider } from '@backstage/theme';
import FlareIcon from '@material-ui/icons/Flare';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import React from 'react';
import { Route } from 'react-router-dom';
import { apis } from './apis';
import { AngularIcon, AtlassianIcon, ConfluenceIcon, DatadogIcon, DevopsIcon, DevopsPipelinesIcon, DevopsRepositoriesIcon, DevopsTestLabIcon, DockerIcon, EntraIcon, GitIcon, GitLabIcon, GoogleCloudIcon, GrafanaIcon, IonicIcon, JiraIcon, MessagingExchangeIcon, MessagingParkingLotIcon, MessagingQueueIcon, MessagingWaitQueueIcon, PostgresqlIcon, SlackIcon, SonarIcon, SpringBootIcon } from './components/icons';
import { EntityPage, HomePage, Root, TotvsSignInPage, RequestPage, NewRequestPage, NewComponentPage, NewDomainStructurePage, NewResourcePage, NewSystemPage, WorkspacesPage, WorkspaceDetailPage, ActivityHistoryPage } from './pages';
import { DisclaimerTextFieldExtension } from './scaffolder/DisclaimerText';
import { MultiEntityPickerRenderedStringExtension } from './scaffolder/MultiEntityPickerRenderedString';
import { EntityPickerRenderedStringExtension } from './scaffolder/EntityPickerRenderedString';
import { RepoAuthorizationFieldExtension } from './scaffolder/RepoAuthorization';
import { SupplyWorkspacePickerExtension } from './scaffolder/SupplyWorkspacePicker';
import { TotvsLightTheme } from './themes';
import { RbacPage } from '@backstage-community/plugin-rbac';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { requestManagerCreatePermission, requestManagerReadPermission } from '@internal/backstage-plugin-request-manager-common';
import { openControlReadPermission } from '@internal/backstage-plugin-supply-features-common';
import { FeedbackPage } from '@internal/backstage-plugin-feedback';
import { ReleaseManagerPage } from '@internal/backstage-plugin-release-manager';

const microsoftAuthProvider: SignInProviderConfig = {
  id: 'microsoft',
  title: 'Microsoft Entra',
  message: 'Sign in using Microsoft Entra',
  apiRef: microsoftAuthApiRef
};

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
    bind(catalogGraphPlugin.externalRoutes, {
      catalogEntity: catalogPlugin.routes.catalogEntity,
    });
  },
  icons: {
    angular: AngularIcon as IconComponent,
    atlassian: AtlassianIcon as IconComponent,
    confluence: ConfluenceIcon as IconComponent,
    datadog: DatadogIcon as IconComponent,
    devops: DevopsIcon as IconComponent,
    entra: EntraIcon as IconComponent,
    devopsPipelines: DevopsPipelinesIcon as IconComponent,
    devopsRepositories: DevopsRepositoriesIcon as IconComponent,
    devopsTestLabIcon: DevopsTestLabIcon as IconComponent,
    docker: DockerIcon as IconComponent,
    git: GitIcon as IconComponent,
    gitLab: GitLabIcon as IconComponent,
    googleCloud: GoogleCloudIcon as IconComponent,
    grafana: GrafanaIcon as IconComponent,
    ionicIcon: IonicIcon as IconComponent,
    jira: JiraIcon as IconComponent,
    messagingExchange: MessagingExchangeIcon as IconComponent,
    messagingParkingLot: MessagingParkingLotIcon as IconComponent,
    messagingQueueIcon: MessagingQueueIcon as IconComponent,
    messagingWaitQueueIcon: MessagingWaitQueueIcon as IconComponent,
    postgresql: PostgresqlIcon as IconComponent,
    slack: SlackIcon as IconComponent,
    sonar: SonarIcon as IconComponent,
    springBoot: SpringBootIcon as IconComponent
  },
  themes: [{
    id: 'theme-totvs-light',
    title: 'TOTVS - Light ',
    variant: 'light',
    icon: <FlareIcon />,
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={TotvsLightTheme}>
        <CssBaseline />
        {children}
      </UnifiedThemeProvider>
    ),
  }],
  components: {
    SignInPage: props => (
      <TotvsSignInPage
      {...props}
      auto
      icon={<EntraIcon />}
      provider={microsoftAuthProvider}
    />
    )
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/activity-history" element={<ActivityHistoryPage />} />
    <Route path="/create" element={<ScaffolderPage/>}>
      <ScaffolderFieldExtensions>
        <MultiEntityPickerRenderedStringExtension />
        <EntityPickerRenderedStringExtension />
        <DisclaimerTextFieldExtension />
        <RepoAuthorizationFieldExtension />
        <SupplyWorkspacePickerExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage initialKind='system' actions={[]} />} />
    <Route path="/catalog/:namespace/:kind/:name" element={<CatalogEntityPage />} >
      {EntityPage}
    </Route>
    <Route path="/catalog-graph" element={
      <CatalogGraphPage
        initialState={{
          selectedKinds: ['domain', 'system', 'group'],
          selectedRelations: [
            RELATION_OWNER_OF,
            RELATION_OWNED_BY,
            RELATION_HAS_PART,
            RELATION_PART_OF,
            RELATION_DEPENDS_ON,
            RELATION_DEPENDENCY_OF
          ],
        }}
      />
    } />
    <Route path="/workspaces/development" element={
      <RequirePermission permission={openControlReadPermission}>
        <WorkspacesPage environment="development" />
      </RequirePermission>
    } />
    <Route path="/workspaces/staging" element={
      <RequirePermission permission={openControlReadPermission}>
        <WorkspacesPage environment="staging" />
      </RequirePermission>
    } />
    <Route path="/workspaces/production" element={
      <RequirePermission permission={openControlReadPermission}>
        <WorkspacesPage environment="production" />
      </RequirePermission>
    } />
    <Route path="/workspaces/:environment/detail/:workspaceId" element={
      <RequirePermission permission={openControlReadPermission}>
        <WorkspaceDetailPage/>
      </RequirePermission>
    } />
    <Route path="/docs" element={<TechDocsIndexPage/>} />
    <Route path="/docs/:namespace/:kind/:name/*" element={<TechDocsReaderPage/>}>
      <TechDocsAddons>
        <VersionSelector/>
      </TechDocsAddons>
    </Route>
    <Route path="/requests" element={
      <RequirePermission permission={requestManagerReadPermission}>
        <RequestPage/>
      </RequirePermission>
    } />
    <Route path="/requests/new-request" element={
      <RequirePermission permission={requestManagerCreatePermission}>
        <NewRequestPage/>
      </RequirePermission>
    } />
    <Route path="/requests/new-request/resource" element={
      <RequirePermission permission={requestManagerCreatePermission}>
        <NewResourcePage/>
      </RequirePermission>
    } />
    <Route path="/requests/new-request/component" element={
      <RequirePermission permission={requestManagerCreatePermission}>
        <NewComponentPage/>
      </RequirePermission>
    } />
    <Route path="/requests/new-request/domain-structure" element={
      <RequirePermission permission={requestManagerCreatePermission}>
        <NewDomainStructurePage/>
      </RequirePermission>
    } />
    <Route path="/requests/new-request/product" element={
      <RequirePermission permission={requestManagerCreatePermission}>
        <NewSystemPage/>
      </RequirePermission>
    } />
    <Route path="/search" element={<SearchPage/>} />
    <Route path="/rbac" element={<RbacPage />} />
    <Route path="/catalog-unprocessed-entities" element={<CatalogUnprocessedEntitiesPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/settings" element={<UserSettingsPage providerSettings={
      <ProviderSettingsItem
        title="Microsoft Entra"
        description="Configure Microsoft Entra settings"
        apiRef={microsoftAuthApiRef}
        icon={EntraIcon as IconComponent} />
    } />} />
    <Route path="/feedback" element={<FeedbackPage />} />
    <Route path="/release-manager" element={<ReleaseManagerPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
